import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch
} from '@mui/material';
import { DatePicker, DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import DownloadIcon from '@mui/icons-material/Download';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Cookies from 'js-cookie';

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100vh',
    padding: '20px',
    gap: '20px',
  },
  controlPanel: {
    padding: '20px',
    backgroundColor: '#1e1e1e',
    borderRadius: '4px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  buttonGroup: {
    display: 'flex',
    gap: '12px',
    flexWrap: 'wrap',
  },
  button: {
    backgroundColor: '#303031',
    '&:hover': {
      backgroundColor: '#404042',
    },
  },
  dateRange: {
    display: 'flex',
    gap: '16px',
    alignItems: 'center',
  },
  tableSection: {
    flex: 1,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#252526',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.15)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(255, 255, 255, 0.25)',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255, 255, 255, 0.15) #252526',
  },
  historyTable: {
    backgroundColor: '#1e1e1e',
    '& .MuiTableCell-head': {
      backgroundColor: '#252526',
      color: '#fff',
    },
  },
  checksList: {
    padding: '4px 8px',
    overflowY: 'auto',
    height: 'calc(100% - 32px)',
    '&::-webkit-scrollbar': {
      width: '4px',
    },
    '&::-webkit-scrollbar-track': {
      background: '#252526',
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'rgba(255, 255, 255, 0.15)',
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: 'rgba(255, 255, 255, 0.25)',
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255, 255, 255, 0.15) #252526',
  },
  dayCell: {
    padding: '0',
    minWidth: '200px',
    height: '400px',
    position: 'relative',
    backgroundColor: '#1e1e1e',
    verticalAlign: 'top',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
  },
  dayHeader: {
    borderBottom: '1px solid rgba(255,255,255,0.1)',
    padding: '4px',
    backgroundColor: '#252526',
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  dateLabel: {
    fontSize: '0.9rem',
    padding: '8px',
    fontWeight: 'bold',
    textAlign: 'center',
    color: '#fff',
  },
  checkEntry: {
    fontSize: '0.8rem',
    padding: '10px',
    margin: '4px 0',
    borderRadius: '4px',
    backgroundColor: '#303031',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff',
  },
  headerRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  typeTimeContainer: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  testType: {
    fontWeight: 'bold',
  },
  timestamp: {
    color: 'rgba(255,255,255,0.7)',
  },
  bottomRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  fullName: {
    color: 'rgba(255,255,255,0.9)',
  },
  status: {
    fontWeight: 'bold',
  },
  statusPass: {
    color: '#4caf50',
  },
  statusFail: {
    color: '#f44336',
  },
  periodSelector: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
  },
  navigationContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
  },
  periodNavigation: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
};

const EsdOverview = () => {
  const [historyData, setHistoryData] = useState([]);
  const [currentWeekDates, setCurrentWeekDates] = useState([]);
  const [lastWeekDates, setLastWeekDates] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    return date;
  });
  const [endDate, setEndDate] = useState(new Date());
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [newEntry, setNewEntry] = useState({
    uuid: '',
    type: 'both',
    status: true,
    timestamp: new Date()
  });

  // Add function to fetch users
  const fetchUsers = async () => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/list_users`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Sort users by full name
        const sortedUsers = data.users.sort((a, b) => {
          const nameA = `${a.fornavn} ${a.etternavn}`.toLowerCase();
          const nameB = `${b.fornavn} ${b.etternavn}`.toLowerCase();
          return nameA.localeCompare(nameB);
        });
        setUsersList(sortedUsers);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // Handle user selection
  const handleUserSelect = (event) => {
    const user = usersList.find(u => u.username === event.target.value);
    setSelectedUser(user);
    if (user) {
      setNewEntry(prev => ({
        ...prev,
        uuid: user.esd_check_uuid || ''
      }));
    }
  };

  // Add useEffect to fetch users when dialog opens
  useEffect(() => {
    if (openAddDialog) {
      fetchUsers();
    }
  }, [openAddDialog]);

  const handleEdit = (entry) => {
    setSelectedEntry(entry);
    setEditDialogOpen(true);
  };

  const handleDelete = async (entryId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne oppføringen?')) {
      return;
    }

    const token = Cookies.get('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/esd/entry/${entryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        fetchHistoryForRange(startDate, endDate);
      } else {
        console.error('Failed to delete entry');
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };

  const handleSaveEdit = async () => {
    if (!selectedEntry) return;

    const token = Cookies.get('token');
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/esd/entry/${selectedEntry.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: selectedEntry.type,
          status: selectedEntry.status,
          timestamp: selectedEntry.timestamp
        })
      });

      if (response.ok) {
        setEditDialogOpen(false);
        setSelectedEntry(null);
        fetchHistoryForRange(startDate, endDate);
      } else {
        console.error('Failed to update entry');
      }
    } catch (error) {
      console.error('Error updating entry:', error);
    }
  };

  const fetchHistoryForRange = async (start, end) => {
    try {
      const token = Cookies.get('token');
      if (!token) {
        console.error('No authentication token found');
        return;
      }

      setLoading(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/esd/history/range?startDate=${start.toISOString().split('T')[0]}&endDate=${end.toISOString().split('T')[0]}`, 
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        setHistoryData(data);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async (entryData) => {
    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/esd/manual-entry`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(entryData)
      });

      if (response.ok) {
        setOpenAddDialog(false);
        fetchHistoryForRange(startDate, endDate);
        setNewEntry({
          uuid: '',
          fullName: '',
          type: 'both',
          status: true,
          timestamp: new Date()
        });
      } else {
        console.error('Failed to add entry');
      }
    } catch (error) {
      console.error('Error adding entry:', error);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this entry?')) return;

    try {
      const token = Cookies.get('token');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/esd/entry/${entryId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      });

      if (response.ok) {
        fetchHistoryForRange(startDate, endDate);
      }
    } catch (error) {
      console.error('Error deleting entry:', error);
    }
  };

  const handleExportExcel = async () => {
    try {
      const token = Cookies.get('token');      
      const formatDate = (date) => {
        // Ensure we have a valid Date object
        const d = date instanceof Date ? date : new Date(date);
        if (isNaN(d.getTime())) {
          throw new Error('Invalid date');
        }
        const day = String(d.getDate()).padStart(2, '0');
        const month = String(d.getMonth() + 1).padStart(2, '0'); // +1 since getMonth() returns 0-11
        const year = d.getFullYear();
        return `${day}.${month}.${year}`;
      };
      
      // Log the dates before formatting
      console.log('Raw dates:', { startDate, endDate });
      
      const startFormatted = formatDate(startDate);
      const endFormatted = formatDate(endDate);
      
      console.log('Attempting export with dates:', { startFormatted, endFormatted });

      const response = await fetch(`${process.env.REACT_APP_API_URL}/esd/export`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          startDate: startFormatted,
          endDate: endFormatted
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.error || 'Export failed';
        alert(errorMessage);
        return;
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `esd-checks-${startFormatted}-${endFormatted}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      // You might want to show this error to the user
    }
  };

  useEffect(() => {
    fetchHistoryForRange(startDate, endDate);
  }, [startDate, endDate]);

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short'
    });
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTestTypeLabel = (type) => {
    switch(type) {
      case 'wristband': return 'Wristband';
      case 'shoes': return 'Shoes';
      case 'both': return 'Wristband & Shoes';
      default: return type;
    }
  };

  const getChecksForDate = (date, history) => {
    if (!date || !history || !Array.isArray(history)) return [];
    
    const startDate = new Date(date);
    startDate.setHours(0, 0, 0, 0);
    
    const endDate = new Date(date);
    endDate.setHours(23, 59, 59, 999);
    
    return history.filter(check => {
      const checkDate = new Date(check.timestamp);
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  useEffect(() => {
    const generateWeekDates = (startOffset = 0) => {
      const dates = [];
      const today = new Date();
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay() + 1 + startOffset);
      
      for (let i = 0; i < 7; i++) {
        const date = new Date(startOfWeek);
        date.setDate(startOfWeek.getDate() + i);
        dates.push(date);
      }
      
      return dates;
    };

    setCurrentWeekDates(generateWeekDates());
    setLastWeekDates(generateWeekDates(-7));
  }, []);

  useEffect(() => {
    fetchHistoryForRange(startDate, endDate);
    const interval = setInterval(fetchHistoryForRange, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const handlePeriodSelect = (days) => {
    const end = new Date();
    const start = new Date();
    start.setDate(end.getDate() - days);
    setStartDate(start);
    setEndDate(end);
  };

  const handlePreviousPeriod = () => {
    const newCurrentWeekStart = new Date(currentWeekDates[0]);
    newCurrentWeekStart.setDate(newCurrentWeekStart.getDate() - 14);
    
    const dates = [];
    const lastWeekDates = [];
    
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(newCurrentWeekStart);
      currentDate.setDate(newCurrentWeekStart.getDate() + i);
      dates.push(currentDate);
      
      const lastWeekDate = new Date(currentDate);
      lastWeekDate.setDate(currentDate.getDate() - 7);
      lastWeekDates.push(lastWeekDate);
    }
    
    setCurrentWeekDates(dates);
    setLastWeekDates(lastWeekDates);
    setStartDate(lastWeekDates[0]);
    setEndDate(dates[6]);
  };

  const handleNextPeriod = () => {
    const newCurrentWeekStart = new Date(currentWeekDates[0]);
    newCurrentWeekStart.setDate(newCurrentWeekStart.getDate() + 14);
    
    const dates = [];
    const lastWeekDates = [];
    
    for (let i = 0; i < 7; i++) {
      const currentDate = new Date(newCurrentWeekStart);
      currentDate.setDate(newCurrentWeekStart.getDate() + i);
      dates.push(currentDate);
      
      const lastWeekDate = new Date(currentDate);
      lastWeekDate.setDate(currentDate.getDate() - 7);
      lastWeekDates.push(lastWeekDate);
    }
    
    setCurrentWeekDates(dates);
    setLastWeekDates(lastWeekDates);
    setStartDate(lastWeekDates[0]);
    setEndDate(dates[6]);
  };

  const renderCheckEntry = (check, checkIndex, editMode) => (
    <Box key={checkIndex} sx={styles.checkEntry}>
      <Box sx={styles.headerRow}>
        <Box sx={styles.typeTimeContainer}>
          <Typography sx={styles.testType}>
            {getTestTypeLabel(check.type)}
          </Typography>
          <Typography sx={styles.timestamp}>
            {formatTime(check.timestamp)}
          </Typography>
        </Box>
        {editMode && (
          <Box>
            <IconButton
              size="small"
              onClick={() => handleEdit(check)}
              sx={{ color: 'rgba(255,255,255,0.7)' }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => handleDelete(check.id)}
              sx={{ color: 'rgba(255,255,255,0.7)' }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        )}
      </Box>
      <Box sx={styles.bottomRow}>
        <Typography sx={styles.fullName}>
          {check.full_name}
        </Typography>
        <Typography sx={{
          ...styles.status,
          ...(check.status ? styles.statusPass : styles.statusFail)
        }}>
          {check.status ? 'PASS' : 'FAIL'}
        </Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={styles.container}>
      <Box sx={styles.controlPanel}>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between">
          <Typography variant="h5">ESD Check Overview</Typography>
          <Box sx={styles.buttonGroup}>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setEditMode(!editMode)}
              sx={{
                ...styles.button,
                backgroundColor: editMode ? '#4caf50' : '#303031',
                '&:hover': {
                  backgroundColor: editMode ? '#45a049' : '#404042',
                }
              }}
            >
              {editMode ? 'Done Editing' : 'Edit Mode'}
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenAddDialog(true)}
              sx={styles.button}
            >
              Add Entry
            </Button>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={styles.button}
            >
              Export Excel
            </Button>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={() => fetchHistoryForRange(startDate, endDate)}
              sx={styles.button}
            >
              Refresh
            </Button>
          </Box>
        </Stack>

        <Box sx={styles.dateRange}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Start Date"
              value={startDate}
              onChange={(newValue) => setStartDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <DatePicker
              label="End Date"
              value={endDate}
              onChange={(newValue) => setEndDate(newValue)}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
      </Box>

      <Box sx={styles.tableSection}>
        <Box sx={styles.navigationContainer}>
          <Box sx={styles.periodNavigation}>
            <IconButton 
              onClick={handlePreviousPeriod}
              sx={styles.button}
            >
              <ChevronLeftIcon />
            </IconButton>
            <Typography>
              {formatDate(lastWeekDates[0])} - {formatDate(currentWeekDates[6])}
            </Typography>
            <IconButton 
              onClick={handleNextPeriod}
              sx={styles.button}
            >
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </Box>
        <TableContainer component={Paper} sx={styles.historyTable}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Last 14 days</TableCell>
                <TableCell align="center">Mon</TableCell>
                <TableCell align="center">Tue</TableCell>
                <TableCell align="center">Wed</TableCell>
                <TableCell align="center">Thu</TableCell>
                <TableCell align="center">Fri</TableCell>
                <TableCell align="center">Sat</TableCell>
                <TableCell align="center">Sun</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>This week</TableCell>
                {currentWeekDates.map((date, index) => {
                  const dayChecks = getChecksForDate(date, historyData);
                  const hasChecks = dayChecks.length > 0;
                  return (
                    <TableCell key={index} sx={styles.dayCell}>
                      <Box sx={styles.dayHeader}>
                        <Typography sx={styles.dateLabel}>
                          {formatDate(date)}
                        </Typography>
                      </Box>
                      <Box sx={styles.checksList}>
                        {hasChecks ? dayChecks.map((check, checkIndex) => 
                          renderCheckEntry(check, checkIndex, editMode)
                        ) : '-'}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
              <TableRow>
                <TableCell>Last week</TableCell>
                {lastWeekDates.map((date, index) => {
                  const dayChecks = getChecksForDate(date, historyData);
                  const hasChecks = dayChecks.length > 0;
                  return (
                    <TableCell key={index} sx={styles.dayCell}>
                      <Box sx={styles.dayHeader}>
                        <Typography sx={styles.dateLabel}>
                          {formatDate(date)}
                        </Typography>
                      </Box>
                      <Box sx={styles.checksList}>
                        {hasChecks ? dayChecks.map((check, checkIndex) => 
                          renderCheckEntry(check, checkIndex, editMode)
                        ) : '-'}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Dialog open={openAddDialog} onClose={() => setOpenAddDialog(false)}>
        <DialogTitle>Add Manual Entry</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Select User</InputLabel>
            <Select
              value={selectedUser ? selectedUser.username : ''}
              onChange={handleUserSelect}
            >
              {usersList.map((user) => (
                <MenuItem key={user.username} value={user.username}>
                  {user.fornavn} {user.etternavn}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="UUID"
            fullWidth
            margin="normal"
            value={newEntry.uuid}
            InputProps={{
              readOnly: true,
            }}
            disabled
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select
              value={newEntry.type}
              onChange={(e) => setNewEntry({...newEntry, type: e.target.value})}
            >
              <MenuItem value="wristband">Wristband</MenuItem>
              <MenuItem value="shoes">Shoes</MenuItem>
              <MenuItem value="both">Both</MenuItem>
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Switch
                checked={newEntry.status}
                onChange={(e) => setNewEntry({...newEntry, status: e.target.checked})}
              />
            }
            label="Status (Pass/Fail)"
            sx={{ marginTop: 2 }}
          />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DateTimePicker
              label="Timestamp"
              value={newEntry.timestamp}
              onChange={(newValue) => setNewEntry({...newEntry, timestamp: newValue})}
              slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
            />
          </LocalizationProvider>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddDialog(false)}>Cancel</Button>
          <Button onClick={() => handleAddEntry(newEntry)} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Rediger ESD-oppføring</DialogTitle>
        <DialogContent>
          {selectedEntry && (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel>Type</InputLabel>
                <Select
                  value={selectedEntry.type}
                  onChange={(e) => setSelectedEntry({...selectedEntry, type: e.target.value})}
                >
                  <MenuItem value="wristband">Wristband</MenuItem>
                  <MenuItem value="shoes">Shoes</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedEntry.status}
                    onChange={(e) => setSelectedEntry({...selectedEntry, status: e.target.checked})}
                  />
                }
                label="Status (Pass/Fail)"
              />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  label="Tidspunkt"
                  value={new Date(selectedEntry.timestamp)}
                  onChange={(newValue) => setSelectedEntry({
                    ...selectedEntry,
                    timestamp: newValue.toISOString()
                  })}
                  slotProps={{ textField: { fullWidth: true, margin: "normal" } }}
                />
              </LocalizationProvider>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Avbryt</Button>
          <Button onClick={handleSaveEdit} variant="contained">Lagre</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EsdOverview;